import React from 'react';
import { MaxRetriesDialog } from './max-retries-dialog';
import { ReadyState, ServerResponses, UIActions, WebsocketAction, WebSocketState } from './types';
import { useWebSocketConnection } from './use-websocket';

export { ReadyState };
export type { UIActions };

export const WebSocketContext = React.createContext<WebSocketState>(undefined!);

type WebSocketProviderProps = {
  uri: string;
  children: React.ReactNode;
  onMessage: (event: string, payload: unknown) => void;
  autoConnect?: boolean;
  maxRetries?: number;
};

export function WebSocketProvider({ uri, children, maxRetries = 5, autoConnect = true, ...props }: WebSocketProviderProps) {
  const { transport, readyState, connect, reset } = useWebSocketConnection(uri, {
    maxRetries,
  });

  const [lastPing, setLastPing] = React.useState<Date | null>(null);

  const isConnecting = React.useMemo(() => {
    return readyState === ReadyState.CONNECTING;
  }, [readyState]);

  const send = React.useCallback(
    function (action: WebsocketAction<UIActions>) {
      if (transport === null || readyState !== ReadyState.OPEN) throw Error('Websocket not open');
      console.log('Frontend action send', action);
      transport.send(JSON.stringify(action));
    },
    [transport, readyState],
  );

  const onMessage = React.useCallback(
    function (this: WebSocket, rawMessage: MessageEvent) {
      try {
        const action: WebsocketAction<ServerResponses> = JSON.parse(rawMessage.data);
        props.onMessage(action.type, action.payload);
        setLastPing(new Date());
      } catch (e) {
        console.log('websocket message rejcted', e);
      }
    },
    [props.onMessage],
  );

  // Connect to the websocket if it is not instantiated
  React.useEffect(() => {
    if (readyState === ReadyState.UNINSTANTIATED && autoConnect) connect();
    else if (readyState === ReadyState.OPEN && lastPing === null) {
      setLastPing(new Date());
    } else if (readyState === ReadyState.CLOSED) {
      setLastPing(null);
    }
  }, [readyState, connect, send, lastPing, autoConnect]);

  // Add event listener to the websocket
  React.useEffect(() => {
    if (!transport) return;
    transport.addEventListener('message', onMessage);
    return () => {
      transport.removeEventListener('message', onMessage);
    };
  }, [transport]);

  const handleReconnect = React.useCallback(() => {
    reset();
  }, [reset]);

  return (
    <WebSocketContext.Provider
      value={{
        readyState,
        isConnecting,
        send,
      }}
    >
      {children}
      <MaxRetriesDialog isOpen={readyState === ReadyState.MAX_RETRIES_REACHED} onReconnect={handleReconnect} />
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = React.useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error('useWebsocket must be used within a Websocket provider');
  }

  return {
    send: context.send,
    connectionState: context.readyState,
  };
}
